'use client';

import {
  AbsoluteFill,
  Sequence,
  interpolate,
  useCurrentFrame,
  Easing,
  staticFile,
} from 'remotion';
import { Audio } from '@remotion/media';
import { useMemo } from 'react';

// Types
import type { EmployeeYearInReview as EmployeeYearInReviewProps } from '@/types';

// Constants
import {
  CLAMP_BOTH,
  DIAGONAL_WIPE_COLLAPSE_DURATION,
  DIAGONAL_WIPE_EXPAND_DURATION,
  DIAGONAL_WIPE_HOLD_DURATION,
  DIAGONAL_WIPE_TOTAL_DURATION,
  EMPLOYEE_YEAR_IN_REVIEW_CONFIG,
  SCENE_3A_3B_OVERLAP_FRAMES,
  SCENE_5B_6_FADE_IN_DURATION,
  SCENE_5_FADE_IN_DURATION,
  SCENE_6_SLIDE_IN_DURATION,
  TRANSITION_BUFFER,
  ZOOM_TRANSITION_DURATION,
  CROSS_ZOOM_TRANSITION_DURATION,
  SCENE_7_ANIMATION_DELAY_OFFSET,
  SCENE_7_8_EXIT_DURATION,
  SCENE_7_8_BACKGROUND_FADE_DELAY,
  SCENE_7_8_BACKGROUND_FADE_DURATION,
  SCENE_7_8_TRANSITION_DURATION,
  SCENE_8_FADE_IN_DURATION,
  SCENE_8_DURATION,
  AUDIO_PATHS,
  AUDIO_VOLUME,
  AUDIO_FADE_OUT_DURATION,
  SCENE_5_DURATION,
} from '@/constants';
import { EXIT_TRANSITION } from './scenes/Scene5a/constants';

// Utils
import { getSceneTransitionFrames } from '@/utils/duration';
import { calculateScene2Duration } from './scenes/Scene2/utils';
import { calculateScene3bDuration } from './scenes/Scene3b/utils';
import {
  calculateTotalScene4Duration,
  getRecentFeedbacks,
} from './scenes/Scene4/utils';
import { calculateScene5bDuration } from './scenes/Scene5b/utils';
import { calculateTotalScene6Duration } from './scenes/Scene6/utils';
import {
  calculateTotalScene7Duration,
  getTopSupportersFromTrainings,
  convertMenteesToTeamMembers,
  convertSupporteesToTeamMembers,
} from './scenes/Scene7/utils';

// Hooks
import {
  PushDirection,
  usePushTransition,
  useCrossZoomTransition,
} from '@/hooks';

// Fonts
import { michromaFontFamily } from '@/styles/fonts';

// Components
import { DiagonalWipeTransition } from '@/components/common/DiagonalWipeTransition';
import { Scene1 } from './scenes/Scene1';
import { Scene2 } from './scenes/Scene2';
import { Scene3a } from './scenes/Scene3a';
import { Scene3b } from './scenes/Scene3b';
import { Scene4 } from './scenes/Scene4';
import { Scene5 } from './scenes/Scene5';
import { Scene5a } from './scenes/Scene5a';
import { Scene5b } from './scenes/Scene5b';
import { Scene5c } from './scenes/Scene5c';
import {
  calculateTotalScene5cDuration,
  getRecentTrainingFeedbacks,
} from './scenes/Scene5c/utils';
import { Scene6 } from './scenes/Scene6';
import { Scene7 } from './scenes/Scene7';
import { Scene8 } from './scenes/Scene8';

// Theme
import { employeeYearInReviewTheme } from './theme';

export const EmployeeYearInReview = ({
  year,
  openingSentence,
  employee,
  monthsOnProject,
  projects,
  projectFeedbacks,
  trainings,
  trainingFeedbacks,
  monthsOnTraining,
  mentees,
  supportees,
}: EmployeeYearInReviewProps) => {
  const frame = useCurrentFrame();
  const {
    timing: { slide1, slide2, slide3a },
    transition,
  } = EMPLOYEE_YEAR_IN_REVIEW_CONFIG;

  // Dynamic Scene 2 duration based on anniversary
  const scene2Duration = calculateScene2Duration(
    employee?.info?.onboardedAt ?? '',
  );
  // Dynamic Scene 3b duration based on project count
  const scene3bDuration = calculateScene3bDuration(projects?.length ?? 0);

  // Filter feedbacks to max 3 items
  const displayFeedbacks = useMemo(
    () => getRecentFeedbacks(projectFeedbacks ?? []),
    [projectFeedbacks],
  );

  // Dynamic Scene 4 duration based on filtered feedback count
  const scene4Duration = useMemo(
    () => calculateTotalScene4Duration(displayFeedbacks),
    [displayFeedbacks],
  );
  const scene5bDuration = useMemo(
    () => calculateScene5bDuration(trainings ?? []),
    [trainings],
  );

  // Filter training feedbacks to max 3 items
  const displayTrainingFeedbacks = useMemo(
    () => getRecentTrainingFeedbacks(trainingFeedbacks ?? []),
    [trainingFeedbacks],
  );

  // Dynamic Scene 5c duration based on filtered training feedback count
  const scene5cDuration = useMemo(
    () => calculateTotalScene5cDuration(displayTrainingFeedbacks),
    [displayTrainingFeedbacks],
  );

  // Dynamic Scene 6a duration based on certificates count
  const scene6Duration = useMemo(
    () => calculateTotalScene6Duration(employee?.certs ?? []),
    [employee?.certs],
  );

  // Extract top supporters from trainings data
  const topSupporters = useMemo(
    () => getTopSupportersFromTrainings(trainings ?? []),
    [trainings],
  );

  // Convert mentees and trainees to TeamMember[] format
  const menteeMembers = useMemo(
    () => convertMenteesToTeamMembers(mentees ?? []),
    [mentees],
  );

  const supporteesMembers = useMemo(
    () => convertSupporteesToTeamMembers(supportees ?? []),
    [supportees],
  );

  // Dynamic Scene 7 duration based on category member counts
  const scene7Duration = useMemo(
    () =>
      calculateTotalScene7Duration([
        topSupporters.length,
        menteeMembers.length,
        supporteesMembers.length,
      ]),
    [topSupporters.length, menteeMembers.length, supporteesMembers.length],
  );

  // Whether to show scenes 3a and 3b (only if projects exist)
  const hasProjects = (projects?.length ?? 0) > 0;
  // Whether to show scene 4 (only if feedback exists)
  const hasFeedback = displayFeedbacks.length > 0;
  // Whether to show scenes 5a and 5b (only if completed trainings exist)
  const hasTrainings = scene5bDuration > 0;
  // Whether to show scene 5c (only if training feedback exists)
  const hasTrainingFeedback = displayTrainingFeedbacks.length > 0;
  // Whether to show scene 6 (only if certificates exist)
  const hasCertificates = (employee?.certs?.length ?? 0) > 0;
  // Whether to show scene 7 (only if it has content)
  const hasScene7 = scene7Duration > 0;

  // Fade out Scene 1 before Scene 2
  const fadeOutStart = slide1.duration - transition.fadeOutDuration;
  const fadeOutEnd = slide1.duration;
  const scene1Opacity = interpolate(frame, [fadeOutStart, fadeOutEnd], [1, 0], {
    ...CLAMP_BOTH,
  });

  // Scene start and end frames
  const {
    scene2End,
    scene3bStart,
    diagonalWipeStartLocal,
    scene4Start,
    scene4End,
    scene5Start,
    scene5aStart,
    scene5aEnd,
    scene5bStart,
    scene5cStart,
    scene6Start,
    scene7Start,
  } = useMemo(
    () =>
      getSceneTransitionFrames(
        scene2Duration,
        scene3bDuration,
        scene4Duration,
        scene5bDuration,
        scene5cDuration,
        scene6Duration,
        scene7Duration,
      ),
    [
      scene2Duration,
      scene3bDuration,
      scene4Duration,
      scene5bDuration,
      scene5cDuration,
      scene6Duration,
      scene7Duration,
    ],
  );

  // Scene 5a: total duration including cross-zoom buffer when skipping certs but showing Scene 7
  const scene5aDuration =
    scene5aEnd -
    scene5aStart +
    (!hasCertificates && hasScene7 ? CROSS_ZOOM_TRANSITION_DURATION : 0);

  // Scene 2: duration including transition buffer for next scene
  const scene2TotalDuration = scene2Duration + TRANSITION_BUFFER;

  // Scene 3b: frame when diagonal wipe expand finishes
  const scene3bDurationUntilWipe =
    diagonalWipeStartLocal + DIAGONAL_WIPE_EXPAND_DURATION;

  // Diagonal wipe: 3b content + overlap + expand + hold + collapse
  const diagonalWipeDuration =
    scene3bDuration + SCENE_3A_3B_OVERLAP_FRAMES + DIAGONAL_WIPE_TOTAL_DURATION;

  // Scene 5a: frame when exit transition starts
  const scene5aExitStartFrame =
    EMPLOYEE_YEAR_IN_REVIEW_CONFIG.timing.slide5a.duration -
    EXIT_TRANSITION.duration;

  // Scene 4: duration including diagonal wipe collapse and transition buffer
  const scene4TotalDuration =
    scene4Duration + DIAGONAL_WIPE_COLLAPSE_DURATION + TRANSITION_BUFFER;

  // Scene 5b: duration including fade-in when certificates scene follows
  const scene5bTotalDuration =
    scene5bDuration + (hasCertificates ? SCENE_5B_6_FADE_IN_DURATION : 0);

  // Scene 5c: training feedback scene duration (only when training feedback exists)
  // When certificates exist, extend by fade-in duration for cross-fade overlap with Scene 6
  const scene5cTotalDuration = hasTrainingFeedback
    ? scene5cDuration + (hasCertificates ? SCENE_6_SLIDE_IN_DURATION : 0)
    : 0;

  // Scene 6: duration including cross-zoom overlap into Scene 7
  const scene6TotalDuration = scene6Duration + CROSS_ZOOM_TRANSITION_DURATION;

  // Scene 7: content duration + cross-zoom in + exit transition to Scene 8
  const scene7TotalDuration =
    scene7Duration +
    CROSS_ZOOM_TRANSITION_DURATION +
    SCENE_7_8_TRANSITION_DURATION;

  // Scene 7: when content animations start (after cross-zoom and delay offset)
  const scene7AnimationDelay =
    CROSS_ZOOM_TRANSITION_DURATION + SCENE_7_ANIMATION_DELAY_OFFSET;

  // Scene 7: frame when exit transition begins
  const scene7ExitStartFrame = scene7Duration + CROSS_ZOOM_TRANSITION_DURATION;

  // Scene 8 (closing): starts immediately after Scene 7 exit, or at Scene 7 start if Scene 7 doesn't exist
  const scene8Start = hasScene7
    ? scene7Start + scene7ExitStartFrame
    : scene7Start;

  // Scene 8: fade-in interpolation
  const scene8FadeIn = interpolate(
    frame,
    [scene8Start, scene8Start + SCENE_8_FADE_IN_DURATION],
    [0, 1],
    CLAMP_BOTH,
  );

  // Total duration
  const totalDuration = scene8Start + SCENE_8_DURATION;

  // Audio fades to silence over last AUDIO_FADE_OUT_DURATION frames
  const audioVolume = (frame: number) =>
    interpolate(
      frame,
      [totalDuration - AUDIO_FADE_OUT_DURATION, totalDuration],
      [AUDIO_VOLUME.backgroundMusic, 0],
      { ...CLAMP_BOTH, easing: Easing.in(Easing.cubic) },
    );

  // Push transition: Scene 2 slides left, Scene 3a (or Scene 5/5a when no projects) slides in from right
  const pushTransitionStart = scene2End - transition.overlapDuration;
  const scene3aStart = scene2End - transition.overlapDuration;
  const { outgoingStyle, incomingStyle } = usePushTransition({
    startFrame: pushTransitionStart,
    duration: transition.overlapDuration + TRANSITION_BUFFER,
    direction: PushDirection.LEFT,
  });

  // Scene 5/5c slides right, Scene 6 slides in from left
  const {
    outgoingStyle: slideOutToScene6Style,
    incomingStyle: scene6SlideInStyle,
  } = usePushTransition({
    startFrame: scene6Start,
    duration: SCENE_6_SLIDE_IN_DURATION,
    direction: PushDirection.RIGHT,
    easing: Easing.bezier(0.25, 0.1, 0.25, 1),
  });

  // Cross Zoom transition: Scene 6 zooms in and fades, Scene 7 zooms out from behind
  const crossZoomStart = scene7Start;
  const {
    outgoingStyle: scene6CrossZoomStyle,
    incomingStyle: scene7CrossZoomStyle,
  } = useCrossZoomTransition({
    startFrame: crossZoomStart,
    duration: CROSS_ZOOM_TRANSITION_DURATION,
  });

  // Scene 4 Animations (only calculated when both projects and feedback exist)
  const scene4Scale =
    hasProjects && hasFeedback
      ? interpolate(frame, [scene5aStart, scene4End], [1, 20], {
          ...CLAMP_BOTH,
          easing: Easing.in(Easing.exp), // Fly-through acceleration
        })
      : 1;

  const scene4Rotate =
    hasProjects && hasFeedback
      ? interpolate(frame, [scene5aStart, scene4End], [0, -10], {
          ...CLAMP_BOTH,
          easing: Easing.inOut(Easing.ease),
        })
      : 0;

  const scene4Opacity =
    hasProjects && hasFeedback
      ? interpolate(
          frame,
          [scene5aStart + ZOOM_TRANSITION_DURATION * 0.8, scene4End],
          [1, 0],
          CLAMP_BOTH,
        )
      : 1;

  // Scene 5 fade-in: wait for Scene 4's zoom+rotate to finish before appearing
  const scene5FadeInStart =
    hasProjects && hasFeedback ? scene4End : scene5Start;
  const scene5FadeIn = !hasTrainings
    ? interpolate(
        frame,
        [scene5FadeInStart, scene5FadeInStart + SCENE_5_FADE_IN_DURATION],
        [0, 1],
        CLAMP_BOTH,
      )
    : 1;

  // Scene 6 fades in from Scene 5b when 5c is absent and trainings exist
  const scene6NeedsFade =
    hasCertificates && !hasTrainingFeedback && hasTrainings;

  const scene6FadeIn = scene6NeedsFade
    ? interpolate(
        frame,
        [scene6Start, scene6Start + SCENE_5B_6_FADE_IN_DURATION],
        [0, 1],
        CLAMP_BOTH,
      )
    : 1;

  // Scene 6 slides in from Scene 5 or 5c
  const scene6UsesSlide =
    hasCertificates && ((hasTrainingFeedback && hasTrainings) || !hasTrainings);

  return (
    <AbsoluteFill style={{ fontFamily: michromaFontFamily }}>
      {/* Background Music */}
      <Audio
        src={staticFile(AUDIO_PATHS.employeeYearInReviewBg)}
        volume={audioVolume}
      />

      {/* Static background */}
      <AbsoluteFill className={employeeYearInReviewTheme.colors.bgPrimary} />

      {/* Scene 1: Intro */}
      <Sequence
        name="Intro"
        from={slide1.start}
        durationInFrames={slide1.duration}
      >
        <AbsoluteFill style={{ opacity: scene1Opacity }}>
          <Scene1 year={year} openingSentence={openingSentence} />
        </AbsoluteFill>
      </Sequence>

      {/* Scene 2: Employee profile (slides out left) */}
      <Sequence
        name="Employee Profile"
        from={slide2.start}
        durationInFrames={scene2TotalDuration}
        className="z-20"
      >
        <AbsoluteFill style={outgoingStyle}>
          <Scene2 employee={employee} />
        </AbsoluteFill>
      </Sequence>

      {/* Scene 3a: Projects (slides in from right) - only shown if projects exist */}
      {hasProjects && (
        <Sequence
          name="Projects Oversight"
          from={scene3aStart}
          durationInFrames={slide3a.duration}
        >
          <AbsoluteFill style={incomingStyle}>
            <Scene3a
              projects={projects ?? []}
              monthsOnProject={monthsOnProject}
            />
          </AbsoluteFill>
        </Sequence>
      )}

      {/* Scene 3b: Project list (until wipe expand) - only shown if projects exist */}
      {hasProjects && (
        <Sequence
          name="List of the project"
          from={scene3bStart}
          durationInFrames={scene3bDurationUntilWipe}
        >
          <Scene3b
            projects={projects ?? []}
            monthsOnProject={monthsOnProject}
            transitionStartFrame={diagonalWipeStartLocal}
          />
        </Sequence>
      )}

      {/* Diagonal Wipe Transition Overlay - only shown if projects exist */}
      {hasProjects && (
        <Sequence
          name="Diagonal Wipe Transition"
          from={scene3bStart}
          durationInFrames={diagonalWipeDuration}
        >
          <DiagonalWipeTransition
            startFrame={diagonalWipeStartLocal}
            expandDuration={DIAGONAL_WIPE_EXPAND_DURATION}
            holdDuration={DIAGONAL_WIPE_HOLD_DURATION}
            collapseDuration={DIAGONAL_WIPE_COLLAPSE_DURATION}
          />
        </Sequence>
      )}

      {/* Scene 5a: Training stats (only when trainings exist) */}
      {hasTrainings && (
        <Sequence
          name="Training Statistics"
          from={scene5aStart}
          durationInFrames={scene5aDuration}
        >
          <AbsoluteFill
            className="z-10 opacity-100"
            style={{
              ...(!hasCertificates && hasScene7 && scene6CrossZoomStyle),
            }}
          >
            <AbsoluteFill
              className="z-20"
              style={{
                ...(hasProjects ? {} : incomingStyle),
              }}
            >
              <Scene5a
                monthsOnTraining={monthsOnTraining}
                trainings={trainings}
                exitStartFrame={scene5aExitStartFrame}
              />
            </AbsoluteFill>
          </AbsoluteFill>
        </Sequence>
      )}

      {/* Scene 4 (revealed under diagonal wipe) - only shown if both projects and feedback exist */}
      {hasProjects && hasFeedback && (
        <Sequence
          name="Project Feedback"
          from={scene4Start}
          durationInFrames={scene4TotalDuration}
        >
          <AbsoluteFill
            style={{
              opacity: scene4Opacity,
              transform: `scale(${scene4Scale}) rotate(${scene4Rotate}deg)`,
              zIndex: 10,
            }}
          >
            <Scene4 animationDelay={10} projectFeedbacks={projectFeedbacks} />
          </AbsoluteFill>
        </Sequence>
      )}

      {/* Scene 5: Empty training (only when NO trainings exist) */}
      {!hasTrainings && (
        <Sequence
          name="Empty Training"
          from={scene5Start}
          durationInFrames={
            SCENE_5_DURATION + (hasCertificates ? SCENE_6_SLIDE_IN_DURATION : 0)
          }
        >
          <AbsoluteFill
            className="z-10"
            style={{
              opacity: scene5FadeIn,
              ...(hasProjects ? {} : incomingStyle),
              ...(!hasCertificates && hasScene7 && scene6CrossZoomStyle),
              ...(hasCertificates && slideOutToScene6Style),
            }}
          >
            <Scene5 />
          </AbsoluteFill>
        </Sequence>
      )}

      {/* Scene 5b: Training highlights (only when trainings exist) */}
      {hasTrainings && (
        <Sequence
          name="Training Highlights"
          from={scene5bStart}
          durationInFrames={scene5bTotalDuration}
        >
          <AbsoluteFill style={{ zIndex: 1 }}>
            <Scene5b trainings={trainings} />
          </AbsoluteFill>
        </Sequence>
      )}

      {/* Scene 5c: Training feedback (only when both trainings and training feedback exist) */}
      {hasTrainings && hasTrainingFeedback && (
        <Sequence
          name="Training Feedback"
          from={scene5cStart}
          durationInFrames={scene5cTotalDuration}
        >
          <AbsoluteFill
            style={{
              zIndex: 3,
              ...(hasCertificates && slideOutToScene6Style),
            }}
          >
            <Scene5c
              animationDelay={10}
              trainingFeedbacks={trainingFeedbacks}
            />
          </AbsoluteFill>
        </Sequence>
      )}

      {/* Scene 6: Certifications - only shown if certificates exist */}
      {hasCertificates && (
        <Sequence
          name="Certifications"
          from={scene6Start}
          durationInFrames={scene6TotalDuration}
        >
          <AbsoluteFill
            style={{
              ...(hasTrainings || hasProjects ? {} : incomingStyle),
              ...scene6CrossZoomStyle,
              // Slide-in from Scene 5/5c, or fade-in from Scene 5b
              ...(scene6UsesSlide
                ? { ...scene6SlideInStyle, zIndex: 2 }
                : {
                    opacity: scene6NeedsFade
                      ? scene6FadeIn *
                        (Number(scene6CrossZoomStyle.opacity) || 1)
                      : Number(scene6CrossZoomStyle.opacity) || 1,
                    ...(scene6NeedsFade && { zIndex: 2 }),
                  }),
            }}
          >
            <Scene6
              certificates={employee?.certs ?? []}
              totalCertificates={employee?.certs?.length ?? 0}
              animationDelay={
                scene6UsesSlide
                  ? SCENE_6_SLIDE_IN_DURATION
                  : SCENE_5B_6_FADE_IN_DURATION
              }
            />
          </AbsoluteFill>
        </Sequence>
      )}

      {/* Scene 7: Supporters / Mentees - only shown if it has content */}
      {hasScene7 && (
        <Sequence
          name="Scene 7"
          from={scene7Start}
          durationInFrames={scene7TotalDuration}
        >
          <AbsoluteFill style={{ zIndex: 2, ...scene7CrossZoomStyle }}>
            <Scene7
              animationDelay={scene7AnimationDelay}
              exitStartFrame={scene7ExitStartFrame}
              exitDuration={SCENE_7_8_EXIT_DURATION}
              bgFadeDelay={SCENE_7_8_BACKGROUND_FADE_DELAY}
              bgFadeDur={SCENE_7_8_BACKGROUND_FADE_DURATION}
              trainings={trainings}
              mentees={mentees}
              supportees={supportees}
            />
          </AbsoluteFill>
        </Sequence>
      )}

      {/* Scene 8: Closing scene — starts directly after Scene 7 exit */}
      <Sequence
        name="Closing scene"
        from={scene8Start}
        durationInFrames={SCENE_8_DURATION}
      >
        <AbsoluteFill
          className="z-20"
          style={{
            opacity: scene8FadeIn,
          }}
        >
          <Scene8 />
        </AbsoluteFill>
      </Sequence>
    </AbsoluteFill>
  );
};
